import { createLogger, LOG_TOPIC } from '@ulta/core/utils/devMode/devMode';

import { GOOGLE_SHOPPING_AGENT_EVENT } from '@ulta/components/ShoppingAgent/ShoppingAgent';

let _logger;
const getLogger = () => {
  if( !_logger ){
    _logger = createLogger( {
      topic: LOG_TOPIC.Sdk,
      scope: '[SDK] ShoppingAgent >'
    } );
  }
  return _logger;
};

/**
 * @desc Internal helper to map a DXL action to the SDK's cart payload format.
 * @param {object} action - The DXL action object.
 * @param {object} action.variables - The variables object.
 * @param {object} action.variables.moduleParams - The module parameters.
 * @param {string} [action.variables.moduleParams.productId] - The ID of the product.
 * @param {string} [action.variables.moduleParams.skuId] - The ID of the SKU.
 * @param {number} [action.variables.moduleParams.quantity=1] - The quantity to add.
 * @returns {object} The SDK-formatted cart payload.
 * @returns {Array<object>} returns.products - List of products.
 * @returns {string} returns.action - The event action.
 * @returns {string} returns.method - The event method.
 */
const mapActionToSdkCartPayload = ( action ) => {
  const { productId = '', skuId = '', quantity = 1 } = action?.variables?.moduleParams || {};

  return {
    products: [{
      productId,
      quantity,
      metadata: { skuId }
    }],
    action: 'EVENT_METHOD_USER_CLICK',
    method: 'add'
  };
};

/**
 * @desc Logic to open the Google Shopping Agent chat.
 * @returns {void}
 */
export const openShoppingAgentChat = () => {
  const agent = typeof window !== 'undefined' ? window.googleAgent : null;

  if( typeof agent?.openChat !== 'function' ){
    getLogger().warn( 'openShoppingAgentChat: agent not available', {
      agentExists: !!agent,
      availableMethods: agent ? Object.keys( agent ) : []
    } );
    return;
  }
  agent.openChat();
};

/**
 * @desc Logic to close the Google Shopping Agent chat.
 * @returns {void}
 */
export const closeShoppingAgentChat = () => {
  const agent = typeof window !== 'undefined' ? window.googleAgent : null;

  if( typeof agent?.closeChat !== 'function' ){
    getLogger().warn( 'closeShoppingAgentChat: agent not available', {
      agentExists: !!agent,
      availableMethods: agent ? Object.keys( agent ) : []
    } );
    return;
  }
  agent.closeChat();
};

/**
 * @desc Higher-order function that returns a callback to open the shopping agent chat.
 * @returns {Function} A callback that calls openShoppingAgentChat when invoked.
 */
export const composeOpenShoppingAgentChat = () => () => {
  openShoppingAgentChat();
};

/**
 * @desc Handles the variant picker close action.
 * @param {object} params - The parameters.
 * @param {Function} params.closeOverlay - Function to close the overlay.
 * @returns {void}
 */
export const handleVariantPickerClose = async( { closeOverlay } ) => {
  closeOverlay?.();
  setTimeout( ()=> {
    openShoppingAgentChat();
  } );

  window.dispatchEvent( new CustomEvent( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, { detail: { aborted: true } } ) );
};

/**
 * @desc Handles the variant picker add to bag action.
 * @param {object} params - The parameters.
 * @param {object} params.action - The action object containing product details.
 * @param {Function} params.closeOverlay - Function to close the overlay.
 * @returns {Promise<void>}
 */
export const handleVariantPickerAddToBag = async( { action, closeOverlay } ) => {
  const payload = mapActionToSdkCartPayload( action );
  try {
    const resultPromise =  global.ULTA_SDK?.shoppingAgentModel?.handlecartaction?.post( payload );

    closeOverlay?.();

    openShoppingAgentChat();

    const res = await resultPromise ;

    if( !( res?.success || res?.data ) ){
      getLogger().error( 'handleVariantPickerAddToBag: SDK returned failure', { res } );
    }

    window.dispatchEvent( new CustomEvent( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, { detail: res } ) );
  }
  catch ( error ){
    getLogger().error( 'handleVariantPickerAddToBag: unexpected error', { error } );
    closeOverlay?.();
    window.dispatchEvent( new CustomEvent( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, { detail: { error, success: false } } ) );
  }
};
